import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../common/Context/AuthContext';
import UploadTabSelector from './DualImageTabs';
import { requestConfigMap } from '../../requestBodyBuilder/RequestBodyBuilder';
import { getApiEndpoint } from '../../../common/getApiEndpoint/GetApiEndPoint';
import { FEATURE_FLAGS } from '../../../common/config/featureFlags';
import {
  getDummyApiResponse,
  transformToNewFormat,
} from '../../../common/config/dummyResponses';
import { MAINTENANCE_MESSAGES } from '../../../common/constant';
import UploadSection from './UploadSection';
import ResultSection from './ResultSection';
import SubmitButton from '../../submitButton/SubmitButton';
import InnerProductSectionHead from './InnerProductSectionHead';
import CreditInfoSection from './CreditInfoSection/CreditInfoSection';
import ProcessingAnimation from './ProcessingAnimation';
import {
  buildDynamicHeaders,
  buildDynamicRequestBody,
  getDynamicApiEndpoint,
} from '../../../common/Utils/dynamicRequestBuilder';
import { TRY_MODE } from '../../../common/Utils/Utils';

const TryThisOut = ({
  currentProduct,
  productConfig,
  mode,
  previewResponseImage,
}) => {
  const [activeTab, setActiveTab] = useState('front');
  const [uploadedFiles, setUploadedFiles] = useState({}); // Store multiple files by fieldName
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [hasProcessed, setHasProcessed] = useState(false); // Track if file has been processed
  const fileInputRefs = useRef({}); // Multiple refs for multiple upload sections
  const fileInputRef = useRef(null);
  const resultSectionRef = useRef(null);
  // const resultSectionRef = useRef(null); // Fixed duplicate variable declaration
  const topContainerRef = useRef(null);
  const { user } = useAuth();

  // DYNAMIC UPLOAD DETECTION
  // Automatically detects upload fields from requestBodySchema
  // - If schema has 1 upload field → 1 upload section
  // - If schema has 2+ upload fields → Multiple upload sections
  const uploadFields =
    productConfig?.requestBodySchema?.filter(
      (field) => field.source === 'Upload' || field.source === 'file'
    ) || [];

  // Log upload configuration for debugging
  useEffect(() => {
    if (uploadFields.length > 0) {
      console.log('📤 Upload Configuration Detected:', {
        uploadFieldsCount: uploadFields.length,
        fields: uploadFields.map((f) => f.fieldName),
        mode: mode,
      });
    }
  }, [uploadFields.length, mode]);

  // Handle file selection for dynamic upload fields
  const handleDynamicFileSelection = (fieldName, file) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const handleFileSelection = (file) => {
    setSelectedFile(file);
    if (mode === TRY_MODE.DUAL) {
      if (activeTab === 'front') {
        setFrontImage(file);
      } else {
        setBackImage(file);
      }
    } else {
      setFrontImage(file);
    }
  };

  const handleFileUpload = async () => {
    setIsProcessing(true);
    try {
      let apiEndpoint, body, headers, method;
      // Check feature flag to determine which configuration to use
      const useDynamicConfig = FEATURE_FLAGS.ENABLE_DYNAMIC_PRODUCT_CONFIG;
      if (
        useDynamicConfig &&
        productConfig &&
        productConfig.requestBodySchema
      ) {
        console.log('✅ Using DYNAMIC configuration (Migration Enabled)');

        // Check if we should use dummy response (feature flag enabled)
        if (FEATURE_FLAGS.USE_DUMMY_RESPONSE) {
          console.log(
            '📦 USE_DUMMY_RESPONSE flag enabled - Using DUMMY API response for demo'
          );

          // Simulate API call delay
          await new Promise((resolve) => setTimeout(resolve, 1500));

          // Get dummy response
          const dummyResponse = getDummyApiResponse(currentProduct.productKey);

          if (dummyResponse) {
            setApiResponse(dummyResponse);
            setIsProcessing(false);
            // Auto-scroll to result section after response is set (only on mobile/tablet)
            setTimeout(() => {
              if (resultSectionRef.current && window.innerWidth <= 1200) {
                resultSectionRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }
            }, 300);
            return;
          } else {
            console.warn(
              '⚠️ No dummy response found for:',
              currentProduct.productKey
            );
          }
        }

        // Build endpoint dynamically
        apiEndpoint = getDynamicApiEndpoint(productConfig, currentProduct);

        // Build request body dynamically
        body = await buildDynamicRequestBody(productConfig, {
          frontImage,
          backImage,
          selectedFile,
          activeTab,
          currentProduct,
          uploadedFiles, // Pass dynamic uploaded files
        });

        // Build headers dynamically
        headers = buildDynamicHeaders(productConfig, currentProduct);
        // Get HTTP method
        method = productConfig.httpMethod || 'POST';
      } else {
        // ⚠️ LEGACY CONFIGURATION (Old Way)
        console.log(
          '⚠️ Using LEGACY hardcoded configuration (Migration Disabled)'
        );

        apiEndpoint = getApiEndpoint(currentProduct, mode, activeTab);

        const config = requestConfigMap[currentProduct.productKey];

        if (!config) {
          throw new Error('No request config found for this product');
        }

        body = await config.buildRequestBody({
          frontImage,
          backImage,
          selectedFile,
          activeTab,
          currentProduct,
        });

        const key = currentProduct?.productKey;
        headers = requestConfigMap[key].getHeaders({ currentProduct });
        method = config.method || 'POST';
      }

      // Make actual API call
      const response = await fetch(apiEndpoint, {
        method,
        body: JSON.stringify(body),
        headers,
      });

      const result = await response.json();

      // Transform old format to new format if needed
      const transformedResult = FEATURE_FLAGS.ENABLE_NEW_RESPONSE_STRUCTURE
        ? transformToNewFormat(result)
        : result;

      setApiResponse(transformedResult);
      setHasProcessed(true); // Mark as processed
      // Auto-scroll to result section after response is set (only on mobile/tablet)
      setTimeout(() => {
        if (resultSectionRef.current && window.innerWidth <= 1200) {
          resultSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error processing image:', error);
      setApiResponse({ error: 'Failed to process image' });
      setHasProcessed(true); // Mark as processed even on error
    } finally {
      setIsProcessing(false);
    }
  };
  const handleReset = () => {
    setSelectedFile(null);
    setApiResponse(null);
    setUploadedFiles({}); // Clear all uploaded files
    setHasProcessed(false); // Reset processed state
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Clear all dynamic file input refs
    Object.values(fileInputRefs.current).forEach((refObj) => {
      if (refObj && refObj.current) {
        refObj.current.value = '';
      }
    });

    // Scroll to top of the product container (only on mobile/tablet)
    setTimeout(() => {
      if (topContainerRef.current && window.innerWidth <= 1200) {
        topContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  useEffect(() => {
    setSelectedFile(null);
    setApiResponse(null);
    setFrontImage(null);
    setBackImage(null);
    setUploadedFiles({}); // Clear uploaded files on tab change
    setHasProcessed(false); // Reset processed state on tab change
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [activeTab]);

  return (
    <>
      <div ref={topContainerRef}>
        {user && FEATURE_FLAGS.APPLICATION_UNDER_MAINTENANCE && (
          <div className="d-flex gap-2 mb-3 mt-2 p-2 text-bg-danger text-bg-warning overflow-hidden ws_nowrp">
            <div className="d-inline-block marquee_anim">
              <span className="fw-bold fs-16 me-4">
                {MAINTENANCE_MESSAGES.REGISTERED_USER}
              </span>
              <span className="fw-bold fs-16 me-4">
                {MAINTENANCE_MESSAGES.REGISTERED_USER}
              </span>
            </div>
          </div>
        )}
        {/* <InnerProductSectionHead currentProduct={currentProduct} /> */}
      </div>
      <div
        className={`position-relative pt-1 pb-3  ${
          mode === TRY_MODE.DUAL ? 'pt-1' : 'pt-lg-4'
        }`}
        style={{ paddingBottom: '80px' }}
      >
        <div className="position-relative">
          {mode === TRY_MODE.DUAL && (
            <UploadTabSelector
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )}
          <CreditInfoSection currentProduct={currentProduct} mode={mode} />
        </div>
        <div className="border-top border-bottom my-2 py-4 resultLayout">
          {/* Show Processing Animation when processing */}
          {isProcessing ? (
            <ProcessingAnimation />
          ) : (
            <div className="row justify-content-center px-3">
              {/* ✅ DYNAMIC UPLOAD SECTIONS - Renders based on requestBodySchema */}
              {FEATURE_FLAGS.ENABLE_NEW_RESPONSE_STRUCTURE &&
              uploadFields.length > 0 ? (
                // Dynamic mode: Render upload section for each upload field
                uploadFields.map((field, index) => {
                  const fieldName = field.fieldName;
                  const uploadedFile = uploadedFiles[fieldName];

                  // Create a ref object for this field if it doesn't exist
                  if (!fileInputRefs.current[fieldName]) {
                    fileInputRefs.current[fieldName] = React.createRef();
                  }

                  return (
                    <div
                      key={fieldName}
                      className={`col-lg-10 col-xl-${
                        uploadFields.length === 1 ? '6' : '6'
                      } pe-lg-4 mb-3 mb-lg-0 uploadComponent`}
                    >
                      {/* <div className="mb-2">
                        <h6 className="font_14 font_600 text-capitalize">
                          {fieldName.replace(/([A-Z])/g, ' $1').trim()}
                        </h6>
                      </div> */}
                      <UploadSection
                        selectedFile={uploadedFile}
                        fileInputRef={fileInputRefs.current[fieldName]}
                        handleFileSelection={(file) =>
                          handleDynamicFileSelection(fieldName, file)
                        }
                        productConfig={productConfig}
                        isDisabled={hasProcessed}
                        mode={mode}
                      />
                    </div>
                  );
                })
              ) : (
                // Legacy mode: Single upload section
                <div className="col-lg-6 col-xl-6 pe-lg-4 mb-3 mb-lg-0 uploadComponent">
                  <UploadSection
                    selectedFile={selectedFile}
                    fileInputRef={fileInputRef}
                    handleFileSelection={handleFileSelection}
                    productConfig={productConfig}
                    isDisabled={hasProcessed}
                    mode={mode}
                  />
                </div>
              )}
              {apiResponse && (
                <div
                  className="col-lg-10 mt-4 mt-xl-0 col-xl-6 autosrollResultSection"
                  ref={resultSectionRef}
                >
                  <h4 className="bg_gray16 font_15 fw-semibold px-2 py-1 rounded text-center">
                    Extracted Details
                  </h4>
                  <ResultSection
                    isProcessing={isProcessing}
                    apiResponse={apiResponse}
                    previewResponseImage={previewResponseImage}
                    mode={mode}
                    selectedFile={selectedFile}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {/* Show buttons if any file is uploaded (dynamic or legacy) */}
        {(selectedFile || Object.keys(uploadedFiles).length > 0) && (
          <div className="p-3 zi_10 d-flex position-sticky bottom-0 start-0 end-0 justify-content-center bg-white gap-3 mt-3">
            <SubmitButton
              style={{ marginTop: '0px' }}
              className="mt-0 rounded-pill btn-master-line"
              onClick={handleReset}
              disabled={isProcessing}
              label="Try Another "
            />
            <SubmitButton
              style={{ marginTop: '0px' }}
              disabled={isProcessing || apiResponse}
              className="mt-0 rounded-pill"
              isLoading={isProcessing}
              onClick={handleFileUpload}
              label="Start Processing"
            />
          </div>
        )}
      </div>
      {/* <OurProducts /> */}
    </>
  );
};

export default TryThisOut;
